import { createApi } from "@reduxjs/toolkit/query/react";
import {
  APP_CONFIG,
  DRIVER_NAME_SEARCH_FIELD,
} from "../../constants/constants";
import { TRecordingDetails } from "../../pages/map-asset/type";
import { localStorageKeys } from "../../utils/localStorageItems";
import { LegacyOrganizationAccount } from "../types/Account";
import {
  BasicEntityListingPayload,
  DeleteOrganizationUserPayload,
  EditOrganizationAccountPayload,
  FleetTrack1LinkResponse,
  LegacyCreateOrganizationUserPayload,
  LegacyCreateOrganizationUserResponse,
  LegacyCreateOrganizationVehiclePayload,
  LegacyDeleteOrganizationDriverPayload,
  LegacyDeleteOrganizationVehiclePayload,
  LegacyEditOrganizationUserPayload,
  LegacyEditOrganizationUserResponse,
  LegacyResendInviteToUserResponse,
  LegacyVehicleRecorderStatusPayload,
  SearchFieldsParams,
  SingleOrganizationUserPayload,
  TAccountFt1AccessPolicy,
  TFt1AccessChoice,
  TUserFt1Access,
} from "../types/Admin";
import {
  LegacyOrganizationAlertListingPayload,
  SingleOrganizationVehicleAlertPayload,
  TLegacyAlert,
  TLegacySingleAlert,
} from "../types/Alert";
import { VisionMaxGetTokenResponse } from "../types/CamerasAdminVM";
import {
  CrashTrackAssetInfoResponse,
  CrashTrackChartDataResponse,
  CrashTrackEventReportResponse,
  CrashTrackIncidentInfoResponse,
  CrashTrackListingPayload,
  CrashTrackTabDataPayload,
  TCrashTrackRecord,
} from "../types/CrashTrack";
import {
  LegacyDocumentEntityListingPayload,
  TDocument,
  TSingleDocumentPayload,
} from "../types/Document";
import {
  CreateOrganizationDriverPayload,
  EditOrganizationDriverPayload,
  LegacyDriverEntityListingPayload,
  OrganizationDriver,
  SingleOrganizationDriverPayload,
  TLegacyDriver,
} from "../types/Driver";
import {
  CreateOrganizationFleettagPayload,
  EditOrganizationFleettagPayload,
  LegacyDeleteOrganizationFleettagPayload,
  LegacyFleettagEntityListingPayload,
  SingleOrganizationFleettagPayload,
  TLegacyFleettag,
} from "../types/Fleettag";
import {
  TAccountStatusCodeColor,
  TGenericStatusCode,
  TUpdateAccountStatusCodeColorPayload,
} from "../types/GenericStatusCode";
import {
  CreateOrganizationGeozonePayload,
  EditOrganizationGeozonePayload,
  LegacyGeozoneEntityListingPayload,
  SingleOrganizationGeozonePayload,
  TLegacyGeozone,
} from "../types/Geozone";
import {
  AssignGeozoneToGeozoneCategoryPayload,
  AssignUserToGeozoneCategoryPayload,
  CreateGeozoneCategoryPayload,
  EditGeozoneCategoryPayload,
  GeozoneCategoryBasePayload,
  LegacyGeozoneCategoryEntityListingPayload,
  SingleOrganizationGeozoneCategoryPayload,
  TLegacyGeozoneCategory,
} from "../types/GeozoneCategory";
import {
  GlobalSearchPayload,
  GlobalSearchResponse,
} from "../types/GlobalSearch";
import {
  CreateOrganizationGroupPayload,
  EditOrganizationGroupPayload,
  EditOrganizationGroupVehiclesPayload,
  EditOrganizationGroupUsersPayload,
  GHListGroupsPayload,
  LegacyGroupEntityListingPayload,
  OrganizationGroup,
  OrgGHCreateGroupPayload,
  OrgGHDeleteGroupPayload,
  OrgGHGroup,
  OrgGHModifyGroupPayload,
  SingleOrganizationGroupPayload,
  TLegacyGroup,
} from "../types/Group";
import {
  THeatmapConfigListingResponse,
  THeatmapEventsPayload,
  THeatmapEventsResponse,
  TUpdateHeatmapConfigPayload,
  TUpdateHeatmapConfigResponse,
} from "../types/Heatmap";
import { ListingResponse } from "../types/Listing";
import { LegacyReportListingPayload } from "../types/Report";
import {
  LegacyCreateOrganizationRulePayload,
  LegacyCreateOrganizationRuleResponse,
  LegacyDeleteOrganizationRulePayload,
  LegacyRuleEntityListingPayload,
  LegacyRuleWithAccount,
  NotificationVariableResponse,
  SingleOrganizationRulePayload,
  TLegacyEditOrganizationRulePayload,
  TLegacyRule,
} from "../types/Rule";
import {
  LegacyUserEntityFilterableListingPayload,
  LegacyUserEntityListingPayload,
  TLegacyUser,
  TLegacyUserWithAccount,
} from "../types/User";
import {
  CategoryACL,
  LegacyDeleteOrganizationRolePayload,
  LegacyEditOrganizatioRolePayload,
  LegacyRoleACLEntityListingPayload,
  LegacyRoleEntityListingPayload,
  OrganizationRoleACLPayload,
  SingleOrganizationRolePayload,
  TAddUsersToRoleResponse,
  TAddUserToRolePayload,
  TCloneRolePayload,
  TEditUserACLPayload,
  TLegacySystemRole,
  TLegacyUserRole,
  TLegacyUserRoleACL,
  TLegacyUserRoleACLCreateModifyPayload,
  TRoleACLCategory,
} from "../types/UserRole";
import {
  AllAssetLocationsPayload,
  AssetLocationDetails,
  LegacyVehicleEntityFilterableListingPayload,
  LegacyVehicleEntityListingPayload,
  LegacyVehiclesEventsListingPayload,
  SingleOrganizationVehiclePayload,
  TLegacyVehicle,
  TLegacyVehicleRecorderMediaToken,
  TLegacyVehicleRecorderStatus,
  VehicleAddZipTiePayload,
  VehicleDecodeVinPayload,
  VehicleDecodeVinResponse,
  VehiclePatchZipTiePayload,
  VehicleZipTieDetails,
} from "../types/Vehicle";
import {
  VehicleMapDataMultiPayload,
  VehicleMapDataMultiResponse,
  VehicleMapDataPayload,
  VehicleMapDataPoint,
  VehicleMapDataResponse,
  VehicleMapMultiEventCountPayload,
  VehicleMapMultiEventCountResponse,
} from "../types/VehicleMap";
import { WhiteLabelConfig, WhiteLabelParam } from "../types/WhiteLabelConfig";
import { appBaseQuery, baseQueryWithAuthHandling } from "../utils/utils";
import { API_METHODS } from "./constants";
import { API_ENDPOINTS } from "./endpoints";
import { handleAuthErrorCode } from "./errorCodes";

export enum AdminApiTags {
  USER_CREATED = "USER_CREATED",
  USER_MODIFIED = "USER_MODIFIED",
  USER_DELETED = "USER_DELETED",
  USER_SINGLE = "USER_SINGLE",
  FT1_ACCESS = "FT1_ACCESS",

  ROLE_CREATED = "ROLE_CREATED",
  ROLE_MODIFIED = "ROLE_MODIFIED",
  ROLE_DELETED = "ROLE_DELETED",
  ROLE_SINGLE = "ROLE_SINGLE",

  ROLE_ACL_MODIFIED = "ROLE_ACL_MODIFIED",

  VEHICLE_CREATED = "VEHICLE_CREATED",
  VEHICLE_MODIFIED = "VEHICLE_MODIFIED",
  VEHICLE_DELETED = "VEHICLE_DELETED",
  VEHICLE_SINGLE = "VEHICLE_SINGLE",

  VEHICLE_RECORDER_STATUS = "VEHICLE_RECORDER_STATUS",

  DRIVER_CREATED = "DRIVER_CREATED",
  DRIVER_MODIFIED = "DRIVER_MODIFIED",
  DRIVER_DELETED = "DRIVER_DELETED",
  DRIVER_SINGLE = "DRIVER_SINGLE",

  GROUP_CREATED = "GROUP_CREATED",
  GROUP_MODIFIED = "GROUP_MODIFIED",
  GROUP_DELETED = "GROUP_DELETED",
  GROUP_SINGLE = "GROUP_SINGLE",

  GH_GROUP_CREATED = "GH_GROUP_CREATED",
  GH_GROUP_MODIFIED = "GH_GROUP_MODIFIED",
  GH_GROUP_DELETED = "GH_GROUP_DELETED",

  FLEETTAG_CREATED = "FLEETTAG_CREATED",
  FLEETTAG_MODIFIED = "FLEETTAG_MODIFIED",
  FLEETTAG_DELETED = "FLEETTAG_DELETED",
  FLEETTAG_SINGLE = "FLEETTAG_SINGLE",

  GEOZONE_CREATED = "GEOZONE_CREATED",
  GEOZONE_MODIFIED = "GEOZONE_MODIFIED",
  GEOZONE_DELETED = "GEOZONE_DELETED",
  GEOZONE_SINGLE = "GEOZONE_SINGLE",

  GEOZONE_CATEGORY_CREATED = "GEOZONE_CATEGORY_CREATED",
  GEOZONE_CATEGORY_MODIFIED = "GEOZONE_CATEGORY_MODIFIED",
  GEOZONE_CATEGORY_DELETED = "GEOZONE_CATEGORY_DELETED",
  GEOZONE_CATEGORY_SINGLE = "GEOZONE_CATEGORY_SINGLE",

  VEHICLE_ALERT_SINGLE = "VEHICLE_ALERT_SINGLE",
  VEHICLE_ZIP_TIE = "VEHICLE_ZIP_TIE",
  ASSET_EVENT_VIDEO_RECORDING = "ASSET_EVENT_VIDEO_RECORDING",

  MANAGE_RULE_ALERT_CREATED = "MANAGE_RULE_ALERT_CREATED",
  MANAGE_RULE_ALERT_MODIFIED = "MANAGE_RULE_ALERT_MODIFIED",
  MANAGE_RULE_ALERT_DELETED = "MANAGE_RULE_ALERT_DELETED",
  MANAGE_RULE_ALERT_SINGLE = "MANAGE_RULE_ALERT_SINGLE",

  DOCUMENTS_CREATED = "DOCUMENTS_CREATED",
  DOCUMENT_MODIFIED = "DOCUMENT_MODIFIED",

  HEATMAP_CONFIG = "HEATMAP_CONFIG",

  STATUS_CODE_COLORS = "STATUS_CODE_COLORS",

  WHITE_LABEL_CREATED = "WHITE_LABEL_CREATED",
  WHITE_LABEL_MODIFIED = "WHITE_LABEL_MODIFIED",
  ENTERPRISE = "ENTERPRISE",
  CUSTOM_ICON_CREATED = "CUSTOM_ICON_CREATED",
}

export const reducerPath = "adminAPI";

const accountHasDriversEnabled = (): boolean => {
  try {
    const ownerOrganizationRaw = localStorage.getItem(
      localStorageKeys.ownerOrganization
    );

    if (!ownerOrganizationRaw) {
      return false;
    }

    const ownerOrganization = JSON.parse(
      ownerOrganizationRaw
    ) as LegacyOrganizationAccount;

    return ownerOrganization?.hasdrivers?.toString() === "1";
  } catch {
    return false;
  }
};

const removeDriverNameSearchFieldAsApplicable = (
  searchFields: string[]
): string[] => {
  if (accountHasDriversEnabled()) {
    return searchFields;
  }

  return searchFields.filter(
    (searchField) => searchField.trim() !== DRIVER_NAME_SEARCH_FIELD
  );
};

export const getAccountConfiguredAssetSearchFields = (): string[] => {
  try {
    const ownerOrganizationRaw = localStorage.getItem(
      localStorageKeys.ownerOrganization
    );

    if (!ownerOrganizationRaw) {
      return APP_CONFIG.ENTITY_SEARCH_FIELDS.DEVICE_LISTING;
    }

    const ownerOrganization = JSON.parse(
      ownerOrganizationRaw
    ) as LegacyOrganizationAccount;

    const accountConfiguredSearchFields =
      ownerOrganization?.asset_searchable_fields
        ?.split(",")
        .map((field) => field.trim().toLowerCase())
        .filter(Boolean) ?? [];

    return accountConfiguredSearchFields.length > 0
      ? accountConfiguredSearchFields
      : APP_CONFIG.ENTITY_SEARCH_FIELDS.DEVICE_LISTING;
  } catch {
    return APP_CONFIG.ENTITY_SEARCH_FIELDS.DEVICE_LISTING;
  }
};

// prepares the search fields params for API
const preparedSearchFieldsForAPI = (searchFields: string[]) => {
  // console.log("===ASF final:", searchFields);
  return searchFields.length > 0
    ? { search_fields: searchFields.join(",") }
    : {};
};

export const getSearchFieldsParams = (
  searchFields: string | null | undefined,
  defaultSearchFields: string[],
  mandatorySearchFields: string[] = []
): SearchFieldsParams => {
  // console.log("==ASF params:", { searchFields, defaultSearchFields });
  if (!searchFields) {
    const filteredDefaultSearchFields =
      removeDriverNameSearchFieldAsApplicable(defaultSearchFields);

    return preparedSearchFieldsForAPI([
      ...filteredDefaultSearchFields,
      ...mandatorySearchFields,
    ]);
  }

  if (searchFields === APP_CONFIG.ENTITY_SEARCH_FIELDS.ALL_FLAG) {
    return {};
  }

  const filteredSearchFields = removeDriverNameSearchFieldAsApplicable(
    searchFields
      .split(",")
      .map((searchField) => searchField.trim())
      .filter(Boolean)
  );

  return preparedSearchFieldsForAPI([
    ...filteredSearchFields,
    ...mandatorySearchFields,
  ]);
};

export const AdminAPIs = createApi({
  reducerPath,
  tagTypes: [
    AdminApiTags.FT1_ACCESS,
    AdminApiTags.USER_CREATED,
    AdminApiTags.USER_MODIFIED,
    AdminApiTags.USER_DELETED,
    AdminApiTags.USER_SINGLE,
    AdminApiTags.CUSTOM_ICON_CREATED,

    AdminApiTags.ROLE_CREATED,
    AdminApiTags.ROLE_MODIFIED,
    AdminApiTags.ROLE_DELETED,
    AdminApiTags.ROLE_SINGLE,

    AdminApiTags.ROLE_ACL_MODIFIED,

    AdminApiTags.VEHICLE_CREATED,
    AdminApiTags.VEHICLE_MODIFIED,
    AdminApiTags.VEHICLE_DELETED,
    AdminApiTags.VEHICLE_SINGLE,

    AdminApiTags.VEHICLE_RECORDER_STATUS,

    AdminApiTags.DRIVER_CREATED,
    AdminApiTags.DRIVER_MODIFIED,
    AdminApiTags.DRIVER_DELETED,
    AdminApiTags.DRIVER_SINGLE,

    AdminApiTags.GROUP_CREATED,
    AdminApiTags.GROUP_MODIFIED,
    AdminApiTags.GROUP_DELETED,
    AdminApiTags.GROUP_SINGLE,

    AdminApiTags.GH_GROUP_CREATED,
    AdminApiTags.GH_GROUP_MODIFIED,
    AdminApiTags.GH_GROUP_DELETED,

    AdminApiTags.FLEETTAG_CREATED,
    AdminApiTags.FLEETTAG_MODIFIED,
    AdminApiTags.FLEETTAG_DELETED,
    AdminApiTags.FLEETTAG_SINGLE,

    AdminApiTags.GEOZONE_CREATED,
    AdminApiTags.GEOZONE_MODIFIED,
    AdminApiTags.GEOZONE_DELETED,
    AdminApiTags.GEOZONE_SINGLE,

    AdminApiTags.GEOZONE_CATEGORY_CREATED,
    AdminApiTags.GEOZONE_CATEGORY_MODIFIED,
    AdminApiTags.GEOZONE_CATEGORY_DELETED,
    AdminApiTags.GEOZONE_CATEGORY_SINGLE,

    AdminApiTags.VEHICLE_ALERT_SINGLE,
    AdminApiTags.VEHICLE_ZIP_TIE,
    AdminApiTags.ASSET_EVENT_VIDEO_RECORDING,

    AdminApiTags.MANAGE_RULE_ALERT_CREATED,
    AdminApiTags.MANAGE_RULE_ALERT_MODIFIED,
    AdminApiTags.MANAGE_RULE_ALERT_DELETED,
    AdminApiTags.MANAGE_RULE_ALERT_SINGLE,

    AdminApiTags.DOCUMENTS_CREATED,
    AdminApiTags.DOCUMENT_MODIFIED,

    AdminApiTags.HEATMAP_CONFIG,

    AdminApiTags.STATUS_CODE_COLORS,

    AdminApiTags.WHITE_LABEL_CREATED,
    AdminApiTags.WHITE_LABEL_MODIFIED,
    AdminApiTags.ENTERPRISE,
  ],
  baseQuery: (args, api, extraOptions) =>
    baseQueryWithAuthHandling(appBaseQuery.withAuth, args, api, extraOptions),
  endpoints: (builder) => ({
    // generate outbound FleetTrack 1.0 Link
    getFleetTrack1Link: builder.query<FleetTrack1LinkResponse, void>({
      query: () => {
        return {
          url: API_ENDPOINTS.FT1_0_LINK,
          method: API_METHODS.GET,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: FleetTrack1LinkResponse) => {
        return response;
      },
    }),

    // roles and permissions
    organizationRolesPermissions: builder.query<TLegacyUserRole[], string>({
      query: (accountid: string) => {
        return {
          url: API_ENDPOINTS.ORGANIZATION.ROLES_PERMISSIONS(accountid),
          method: API_METHODS.GET,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: TLegacyUserRole[]) => {
        return response;
      },
    }),

    // create organization user
    createOrganizationUser: builder.mutation<
      LegacyCreateOrganizationUserResponse,
      LegacyCreateOrganizationUserPayload
    >({
      query: (params: LegacyCreateOrganizationUserPayload) => {
        return {
          url: API_ENDPOINTS.ORGANIZATION.INVITE_USER,
          method: API_METHODS.POST,
          body: params,
        };
      },
      invalidatesTags: [AdminApiTags.USER_CREATED],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: LegacyCreateOrganizationUserResponse) => {
        return response;
      },
    }),

    // resend invite to organization user
    resendInviteToOrgUser: builder.mutation<
      LegacyResendInviteToUserResponse,
      LegacyCreateOrganizationUserPayload
    >({
      query: (params: LegacyCreateOrganizationUserPayload) => {
        return {
          url: API_ENDPOINTS.ORGANIZATION.RESEND_INVITE,
          method: API_METHODS.POST,
          body: params,
        };
      },
      // invalidatesTags: [AdminApiTags.USER_CREATED],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: LegacyResendInviteToUserResponse) => {
        return response;
      },
    }),

    // organization users
    organizationUsers: builder.query<
      ListingResponse<TLegacyUserWithAccount[]>,
      LegacyUserEntityListingPayload
    >({
      query: ({ search_fields, ...params }: LegacyUserEntityListingPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.ORGANIZATION_USERS,
          params: {
            ...params,
            ...getSearchFieldsParams(
              search_fields,
              APP_CONFIG.ENTITY_SEARCH_FIELDS.USER_LISTING
            ),
          },
          method: API_METHODS.GET,
        };
      },
      providesTags: [
        AdminApiTags.USER_CREATED,
        AdminApiTags.USER_MODIFIED,
        AdminApiTags.USER_DELETED,
      ],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (
        response: ListingResponse<TLegacyUserWithAccount[]>
      ) => {
        return response;
      },
    }),

    // organization users - filterable (with exclude option)
    organizationUsersFilterable: builder.query<
      ListingResponse<TLegacyUserWithAccount[]>,
      LegacyUserEntityFilterableListingPayload
    >({
      query: ({
        exclude_ids,
        exclude_all_groups_users,
        search_fields,
        ...params
      }: LegacyUserEntityFilterableListingPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.ORGANIZATION_USERS_FILTERABLE,
          params: {
            ...params,
            ...getSearchFieldsParams(
              search_fields,
              APP_CONFIG.ENTITY_SEARCH_FIELDS.USER_LISTING
            ),
          },
          method: API_METHODS.POST,
          body: { exclude_ids, exclude_all_groups_users },
        };
      },
      providesTags: [
        AdminApiTags.USER_CREATED,
        AdminApiTags.USER_MODIFIED,
        AdminApiTags.USER_DELETED,
      ],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (
        response: ListingResponse<TLegacyUserWithAccount[]>
      ) => {
        return response;
      },
    }),

    // single organization user
    singleOrganizationUser: builder.query<
      TLegacyUserWithAccount,
      SingleOrganizationUserPayload
    >({
      query: ({ account_id, user_id }) => {
        return {
          url: API_ENDPOINTS.ADMINS.SINGLE_ORGANIZATION_USER(
            account_id,
            user_id
          ),
          method: API_METHODS.GET,
        };
      },
      providesTags: [AdminApiTags.USER_SINGLE],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: TLegacyUserWithAccount) => {
        return response;
      },
    }),

    // FT1 access after FT2 registration: account policy
    accountFt1AccessPolicy: builder.query<
      TAccountFt1AccessPolicy,
      { account_id: string }
    >({
      query: (params) => ({
        url: API_ENDPOINTS.ORGANIZATION.ACCOUNT_ACCESS_POLICY,
        method: API_METHODS.GET,
        params,
      }),
      providesTags: [AdminApiTags.FT1_ACCESS],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
    }),
    editAccountFt1AccessPolicy: builder.mutation<
      TAccountFt1AccessPolicy,
      Pick<
        TAccountFt1AccessPolicy,
        "account_id" | "allow_ft1_after_ft2_registration"
      >
    >({
      query: (body) => ({
        url: API_ENDPOINTS.ORGANIZATION.ACCOUNT_ACCESS_POLICY,
        method: API_METHODS.PUT,
        body,
      }),
      invalidatesTags: [AdminApiTags.FT1_ACCESS],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
    }),

    // FT1 access after FT2 registration: per-user override
    userFt1Access: builder.query<
      TUserFt1Access,
      { account_id: string; user_id: string }
    >({
      query: (params) => ({
        url: API_ENDPOINTS.ORGANIZATION.USER_FT1_ACCESS,
        method: API_METHODS.GET,
        params,
      }),
      providesTags: [AdminApiTags.FT1_ACCESS],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
    }),
    editUserFt1Access: builder.mutation<
      TUserFt1Access,
      { account_id: string; user_id: string; ft1_access: TFt1AccessChoice }
    >({
      query: (body) => ({
        url: API_ENDPOINTS.ORGANIZATION.USER_FT1_ACCESS,
        method: API_METHODS.PUT,
        body,
      }),
      invalidatesTags: [AdminApiTags.FT1_ACCESS],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
    }),

    // edit organization user
    editOrganizationUser: builder.mutation<
      LegacyEditOrganizationUserResponse,
      LegacyEditOrganizationUserPayload
    >({
      query: (params: LegacyEditOrganizationUserPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.EDIT_ORGANIZATION_USER,
          method: API_METHODS.PATCH,
          body: params,
        };
      },
      invalidatesTags: [
        AdminApiTags.USER_MODIFIED,
        AdminApiTags.USER_SINGLE,
        AdminApiTags.VEHICLE_MODIFIED, // invalidates vehicle listing and vehicle events - to refetch vehicle data if user groups are changed
        AdminApiTags.GROUP_MODIFIED, // invalidates group listing - to refetch group data if user groups are changed
      ],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: LegacyEditOrganizationUserResponse) => {
        return response;
      },
    }),

    // delete single user
    deleteSingleUser: builder.mutation<void, DeleteOrganizationUserPayload>({
      query: ({ email }) => {
        return {
          url: API_ENDPOINTS.ADMINS.DELETE_ORGANIZATION_USER(email),
          method: API_METHODS.DELETE,
        };
      },
      invalidatesTags: [AdminApiTags.USER_DELETED],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: void) => {
        return response;
      },
    }),

    // organization system roles
    organizationSystemRoles: builder.query<TLegacySystemRole[], void>({
      query: () => {
        return {
          url: API_ENDPOINTS.ADMINS.SYSTEM_ROLES,
          method: API_METHODS.GET,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: TLegacySystemRole[]) => {
        return response;
      },
    }),

    // create organization role
    createOrganizationRole: builder.mutation<TLegacyUserRole, TLegacyUserRole>({
      query: (params: TLegacyUserRole) => {
        return {
          url: API_ENDPOINTS.ADMINS.ROLES,
          method: API_METHODS.POST,
          body: params,
        };
      },
      invalidatesTags: [AdminApiTags.ROLE_CREATED],
      transformErrorResponse(baseQueryReturnValue) {
        // disabled error handling here, instead it is now handled in `createNewRole.tsx` file
        // handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: TLegacyUserRole) => {
        return response;
      },
    }),

    // clone organization role
    cloneOrganizationRole: builder.mutation<TLegacyUserRole, TCloneRolePayload>(
      {
        query: (params: TCloneRolePayload) => {
          return {
            url: API_ENDPOINTS.ADMINS.CLONE_ROLE,
            method: API_METHODS.POST,
            body: params,
          };
        },
        invalidatesTags: [AdminApiTags.ROLE_CREATED],
        transformErrorResponse(baseQueryReturnValue) {
          // disabled error handling here, instead it is now handled in `createNewRole.tsx` file
          // handleAuthErrorCode(baseQueryReturnValue);
          return baseQueryReturnValue;
        },
        transformResponse: (response: TLegacyUserRole) => {
          return response;
        },
      }
    ),

    // organization roles
    organizationRoles: builder.query<
      ListingResponse<TLegacyUserRole[]>,
      LegacyRoleEntityListingPayload
    >({
      query: ({ search_fields, ...params }: LegacyRoleEntityListingPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.ROLES,
          params: {
            ...params,
            ...getSearchFieldsParams(
              search_fields,
              APP_CONFIG.ENTITY_SEARCH_FIELDS.ROLE_LISTING
            ),
          },
          method: API_METHODS.GET,
        };
      },
      providesTags: [
        AdminApiTags.ROLE_CREATED,
        AdminApiTags.ROLE_MODIFIED,
        AdminApiTags.ROLE_DELETED,
      ],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: ListingResponse<TLegacyUserRole[]>) => {
        return response;
      },
    }),

    // single organization role
    singleOrganizationRole: builder.query<
      TLegacyUserRole,
      SingleOrganizationRolePayload
    >({
      query: (params: SingleOrganizationRolePayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.SINGLE_ROLE,
          params,
          method: API_METHODS.GET,
        };
      },
      providesTags: [AdminApiTags.ROLE_SINGLE],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: TLegacyUserRole) => {
        return response;
      },
    }),

    // edit organization role
    editOrganizationRole: builder.mutation<
      TLegacyUserRole,
      LegacyEditOrganizatioRolePayload
    >({
      query: (params: LegacyEditOrganizatioRolePayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.EDIT_ROLE,
          method: API_METHODS.PATCH,
          body: params,
        };
      },
      invalidatesTags: [
        AdminApiTags.ROLE_MODIFIED,
        AdminApiTags.ROLE_SINGLE,
        AdminApiTags.ROLE_ACL_MODIFIED,
      ],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: TLegacyUserRole) => {
        return response;
      },
    }),

    // edit organization user
    editOrganizationUserACL: builder.mutation<any, TEditUserACLPayload>({
      query: (params: TEditUserACLPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.EDIT_USER_ACL,
          method: API_METHODS.POST,
          body: params,
        };
      },
      invalidatesTags: [
        AdminApiTags.USER_MODIFIED,
        AdminApiTags.USER_SINGLE,
        AdminApiTags.ROLE_ACL_MODIFIED,
      ],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: any) => {
        return response;
      },
    }),

    // delete organization role
    deleteOrganiaztionRole: builder.mutation<
      void,
      LegacyDeleteOrganizationRolePayload
    >({
      query: ({ accountid, roleid }: LegacyDeleteOrganizationRolePayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.DELETE_ROLE(accountid, roleid),
          method: API_METHODS.DELETE,
        };
      },
      invalidatesTags: [AdminApiTags.ROLE_DELETED],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: void) => {
        return response;
      },
    }),
    // Organisation role ACL category list(New)
    organizationRoleCategoryList: builder.query<
      ListingResponse<TRoleACLCategory[]>,
      BasicEntityListingPayload
    >({
      query: (params: BasicEntityListingPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.ROLE_ACL_CATEGORY,
          method: API_METHODS.GET,
          params,
        };
      },
      providesTags: [AdminApiTags.ROLE_ACL_MODIFIED],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: ListingResponse<TRoleACLCategory[]>) => {
        return response;
      },
    }),
    // Organisation role ACL list(New)
    organizationRoleCategoryACLList: builder.query<
      ListingResponse<CategoryACL[]>,
      OrganizationRoleACLPayload
    >({
      query: (params: OrganizationRoleACLPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.CATEGORY_ACL,
          method: API_METHODS.GET,
          params: params,
        };
      },
      providesTags: [AdminApiTags.ROLE_ACL_MODIFIED],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: ListingResponse<CategoryACL[]>) => {
        return response;
      },
    }),
    // organization role ACL
    organizationRoleACL: builder.query<
      TLegacyUserRoleACL[],
      LegacyRoleACLEntityListingPayload
    >({
      query: (params: LegacyRoleACLEntityListingPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.ROLE_ACL(
            `${params.accountid}`,
            `${params.roleid}`
          ),
          method: API_METHODS.GET,
        };
      },
      providesTags: [AdminApiTags.ROLE_ACL_MODIFIED],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: TLegacyUserRoleACL[]) => {
        return response;
      },
    }),

    // create / modify organization role ACL
    createModifyOrganizationRoleACL: builder.mutation<
      ListingResponse<TLegacyUserRoleACL[]>,
      TLegacyUserRoleACLCreateModifyPayload
    >({
      query: ({ mode, ...params }: TLegacyUserRoleACLCreateModifyPayload) => {
        return {
          url:
            mode === "create"
              ? API_ENDPOINTS.ADMINS.CREATE_ROLE_ACL
              : API_ENDPOINTS.ADMINS.EDIT_ROLE_ACL,
          method: mode === "create" ? API_METHODS.POST : API_METHODS.PATCH,
          body: params,
        };
      },
      invalidatesTags: [AdminApiTags.ROLE_ACL_MODIFIED],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: ListingResponse<TLegacyUserRoleACL[]>) => {
        return response;
      },
    }),

    // organization vehicles
    organizationVehicles: builder.query<
      ListingResponse<TLegacyVehicle[]>,
      LegacyVehicleEntityListingPayload
    >({
      query: ({
        search_fields,
        ...params
      }: LegacyVehicleEntityListingPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.ORGANIZATION_VEHICLES,
          params: {
            ...params,
            ...getSearchFieldsParams(
              search_fields,
              getAccountConfiguredAssetSearchFields(),
              APP_CONFIG.ASSET_CONFIGS.MANDATORY_SEARCH_FIELDS
            ),
          },
          method: API_METHODS.GET,
        };
      },
      providesTags: [
        AdminApiTags.VEHICLE_CREATED,
        AdminApiTags.VEHICLE_MODIFIED,
        AdminApiTags.VEHICLE_DELETED,
      ],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: ListingResponse<TLegacyVehicle[]>) => {
        return response;
      },
    }),

    // validate Unique ID
    validateUniqueId: builder.mutation<
      any,
      { unique_id: string; device_id?: string }
    >({
      query: (data) => {
        return {
          url: API_ENDPOINTS.ADMINS.UNIQUE_ID_VALIDATIONS,
          method: API_METHODS.POST,
          body: data,
          params: { time: Date.now() /* to avoid caching */ },
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        return baseQueryReturnValue;
      },
      transformResponse: (response: any) => {
        return response;
      },
    }),

    // organization vehicles - filterable (with exclude option)
    organizationVehiclesFilterable: builder.query<
      ListingResponse<TLegacyVehicle[]>,
      LegacyVehicleEntityFilterableListingPayload
    >({
      query: ({
        exclude_ids,
        search_fields,
        ...params
      }: LegacyVehicleEntityFilterableListingPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.ORGANIZATION_VEHICLES_FILTERABLE,
          params: {
            ...params,
            ...getSearchFieldsParams(
              search_fields,
              getAccountConfiguredAssetSearchFields(),
              APP_CONFIG.ASSET_CONFIGS.MANDATORY_SEARCH_FIELDS
            ),
          },
          method: API_METHODS.POST,
          body: { exclude_ids },
        };
      },
      providesTags: [
        AdminApiTags.VEHICLE_CREATED,
        AdminApiTags.VEHICLE_MODIFIED,
        AdminApiTags.VEHICLE_DELETED,
      ],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: ListingResponse<TLegacyVehicle[]>) => {
        return response;
      },
    }),

    // organization vehicles' events
    organizationVehiclesEvents: builder.query<
      VehicleMapDataPoint[] /* TLegacyVehicleEvent[] */,
      LegacyVehiclesEventsListingPayload
    >({
      query: ({
        account_id,
        ...restParams
      }: LegacyVehiclesEventsListingPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.ORGANIZATION_VEHICLES_EVENTS(account_id),
          params: restParams,
          method: API_METHODS.GET,
        };
      },
      // keepUnusedDataFor: 60 * 60 * 2, // 2 hours
      providesTags: [
        AdminApiTags.VEHICLE_CREATED,
        AdminApiTags.VEHICLE_MODIFIED,
        AdminApiTags.VEHICLE_DELETED,
      ],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: VehicleMapDataPoint[]) => {
        // TLegacyVehicleEvent[]
        return response;
      },
    }),

    allAssetLocations: builder.query<
      AssetLocationDetails[],
      AllAssetLocationsPayload
    >({
      query: (params: AllAssetLocationsPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.ALL_ASSET_LOCATIONS,
          method: API_METHODS.GET,
          params,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: AssetLocationDetails[]) => {
        return response;
      },
    }),

    // create organization vehicle
    createOrganizationVehicle: builder.mutation<
      TLegacyVehicle,
      LegacyCreateOrganizationVehiclePayload
    >({
      query: (params: LegacyCreateOrganizationVehiclePayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.ORGANIZATION_VEHICLES,
          method: API_METHODS.POST,
          body: params,
        };
      },
      invalidatesTags: [AdminApiTags.VEHICLE_CREATED],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: TLegacyVehicle) => {
        return response;
      },
    }),

    // single organization vehicle
    singleOrganizationVehicle: builder.query<
      TLegacyVehicle,
      SingleOrganizationVehiclePayload
    >({
      query: (params: SingleOrganizationVehiclePayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.SINGLE_ORGANIZATION_VEHICLE,
          params,
          method: API_METHODS.GET,
        };
      },
      providesTags: [AdminApiTags.VEHICLE_SINGLE],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: TLegacyVehicle) => {
        return response;
      },
    }),

    //edit organization vehicle
    editOrganizationVehicle: builder.mutation<
      TLegacyVehicle,
      Partial<TLegacyVehicle>
    >({
      // TODO: change the type
      query: (params: Partial<TLegacyVehicle>) => {
        return {
          url: API_ENDPOINTS.ADMINS.EDIT_ORGANIZATION_VEHICLE,
          // params: { account_id: params.account_id, device_id: params.device_id },
          method: API_METHODS.PATCH,
          body: params,
        };
      },
      invalidatesTags: [
        AdminApiTags.VEHICLE_MODIFIED,
        AdminApiTags.VEHICLE_SINGLE,
        AdminApiTags.GROUP_MODIFIED,
        AdminApiTags.GROUP_SINGLE,
      ],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: TLegacyVehicle) => {
        return response;
      },
    }),

    //edit organization vehicle image
    editOrganizationVehicleImage: builder.mutation<any, FormData>({
      query: (formData: FormData) => {
        return {
          url: API_ENDPOINTS.ADMINS.EDIT_ORGANIZATION_VEHICLE_IMAGE,
          method: API_METHODS.PATCH,
          body: formData,
          formData: true,
        };
      },
      invalidatesTags: [
        AdminApiTags.VEHICLE_MODIFIED,
        AdminApiTags.VEHICLE_SINGLE,
        AdminApiTags.GROUP_MODIFIED,
        AdminApiTags.GROUP_SINGLE,
      ],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: any) => {
        return response;
      },
    }),

    // get vehicle recorder status
    vehicleRecorderStatus: builder.query<
      TLegacyVehicleRecorderStatus,
      LegacyVehicleRecorderStatusPayload
    >({
      query: (params: LegacyVehicleRecorderStatusPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.VEHICLE_RECORDER_STATUS(
            params.account_id,
            params.device_id
          ),
          method: API_METHODS.GET,
        };
      },
      providesTags: [AdminApiTags.VEHICLE_RECORDER_STATUS],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: TLegacyVehicleRecorderStatus) => {
        return response;
      },
    }),

    // get vehicle recorder media token - not in use as of now because we are directly using AT with lytx-live-video
    vehicleRecorderMediaToken: builder.query<
      TLegacyVehicleRecorderMediaToken,
      LegacyVehicleRecorderStatusPayload
    >({
      query: (params: LegacyVehicleRecorderStatusPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.VEHICLE_RECORDER_MEDIA_TOKEN(
            params.account_id,
            params.device_id
          ),
          method: API_METHODS.GET,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: TLegacyVehicleRecorderMediaToken) => {
        return response;
      },
    }),

    //delete organization vehicle
    deleteSingleVehicle: builder.mutation<
      void,
      LegacyDeleteOrganizationVehiclePayload
    >({
      query: (params: LegacyDeleteOrganizationVehiclePayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.DELETE_ORGANIZATION_VEHICLE(
            params.account_id,
            params.device_id
          ),
          // params: { organization_id },
          method: API_METHODS.DELETE,
        };
      },
      invalidatesTags: [AdminApiTags.VEHICLE_DELETED],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: void) => {
        return response;
      },
    }),

    //get vehicle zip ties
    vehicleZipTies: builder.query<
      ListingResponse<VehicleZipTieDetails[]>,
      string
    >({
      query: (device_id) => {
        return {
          url: API_ENDPOINTS.ADMINS.VEHICLE_GET_DEVICE_ZIPTIE(device_id),
          method: API_METHODS.GET,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: ListingResponse<VehicleZipTieDetails[]>) => {
        return response;
      },
      providesTags: [AdminApiTags.VEHICLE_ZIP_TIE]
    }),

    //patch vehicle zip ties
    patchVehicleZipTie: builder.mutation<
      VehicleZipTieDetails,
      VehiclePatchZipTiePayload
    >({
      query: (params: VehiclePatchZipTiePayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.VEHICLE_DEVICE_ZIPTIE(params.zip_tie_id.toString()),
          method: API_METHODS.PATCH,
          body: { serial_number: params.serial_number },
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: VehicleZipTieDetails) => {
        return response;
      },
      invalidatesTags: [AdminApiTags.VEHICLE_ZIP_TIE]
    }),

    //post vehicle zip tie
    postVehicleZipTie: builder.mutation<
      VehicleZipTieDetails,
      VehicleAddZipTiePayload
    >({
      query: (params: VehicleAddZipTiePayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.VEHICLE_ADD_DEVICE_ZIPTIE,
          method: API_METHODS.POST,
          body: params,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: VehicleZipTieDetails) => {
        return response;
      },
      invalidatesTags: [AdminApiTags.VEHICLE_ZIP_TIE]
    }),

    //delete vehicle zip ties
    deleteVehicleZipTie: builder.mutation<
      void,
      VehiclePatchZipTiePayload
    >({
      query: (params: VehiclePatchZipTiePayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.VEHICLE_DEVICE_ZIPTIE(params.zip_tie_id.toString()),
          method: API_METHODS.DELETE,
          // body: { zip_tie_id: params.zip_tie_id },    
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: void) => {
        return response;
      },
      invalidatesTags: [AdminApiTags.VEHICLE_ZIP_TIE]
    }),

    // vehile map data
    vehicleMapData: builder.query<
      VehicleMapDataResponse,
      VehicleMapDataPayload
    >({
      query: (params: VehicleMapDataPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.VEHICLE_MAP_DATA(
            params.account_id,
            params.device_id,
            params.start_timestamp,
            params.end_timestamp,
            params.sort_time
          ),
          method: API_METHODS.GET,
        };
      },
      // providesTags: [AdminApiTags.VEHICLE_SINGLE],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: VehicleMapDataResponse) => {
        return response;
      },
    }),

    // vehicle map data - multiple vehicles
    vehicleMapDataMulti: builder.query<
      VehicleMapDataMultiResponse,
      VehicleMapDataMultiPayload
    >({
      query: (params: VehicleMapDataMultiPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.VEHICLE_MAP_DATA_MULTI(
            params.start_timestamp,
            params.end_timestamp,
            params.deviceIds,
            params.sort_time
          ),
          method: API_METHODS.GET,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        return baseQueryReturnValue;
      },
      transformResponse: (response: VehicleMapDataMultiResponse) => {
        return response;
      },
    }),

    // how many events a multi-vehicle selection would pull, asked before
    // the plot so an over-cap selection is caught in the selector rather
    // than by the map request failing
    vehicleMapDataMultiEventCount: builder.query<
      VehicleMapMultiEventCountResponse,
      VehicleMapMultiEventCountPayload
    >({
      query: (params: VehicleMapMultiEventCountPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.VEHICLE_MAP_DATA_MULTI_EVENT_COUNT,
          method: API_METHODS.POST,
          body: params,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        return baseQueryReturnValue;
      },
      transformResponse: (response: VehicleMapMultiEventCountResponse) => {
        return response;
      },
    }),

    // vehile decode vin
    vehicleDecodeVin: builder.query<
      VehicleDecodeVinResponse,
      VehicleDecodeVinPayload
    >({
      query: (params: VehicleDecodeVinPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.VEHICLE_DECODE_VIN(params?.asset_vin),
          method: API_METHODS.GET,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: VehicleDecodeVinResponse) => {
        return response;
      },
    }),

    // organization drivers
    organizationDrivers: builder.query<
      ListingResponse<TLegacyDriver[]>,
      LegacyDriverEntityListingPayload
    >({
      // TODO: change the type
      query: ({
        search_fields,
        ...params
      }: LegacyDriverEntityListingPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.ORGANIZATION_DRIVERS,
          params: {
            ...params,
            ...getSearchFieldsParams(
              search_fields,
              APP_CONFIG.ENTITY_SEARCH_FIELDS.DRIVER_LISTING
            ),
          },
          method: API_METHODS.GET,
        };
      },
      providesTags: [
        AdminApiTags.DRIVER_CREATED,
        AdminApiTags.DRIVER_MODIFIED,
        AdminApiTags.DRIVER_DELETED,
      ],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: ListingResponse<TLegacyDriver[]>) => {
        return response;
      },
    }),

    // create organization driver
    createOrganizationDriver: builder.mutation<
      TLegacyDriver,
      CreateOrganizationDriverPayload
    >({
      query: (body: CreateOrganizationDriverPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.ORGANIZATION_DRIVERS,
          method: API_METHODS.POST,
          body,
        };
      },
      invalidatesTags: [AdminApiTags.DRIVER_CREATED],
      transformErrorResponse(baseQueryReturnValue) {
        // disabled error handling here, instead it is now handled in `createNewDriver.tsx` file
        // handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: TLegacyDriver) => {
        return response;
      },
    }),

    // single organization driver
    singleOrganizationDriver: builder.query<
      TLegacyDriver,
      SingleOrganizationDriverPayload
    >({
      query: (params: SingleOrganizationDriverPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.SINGLE_ORGANIZATION_DRIVER,
          params,
          method: API_METHODS.GET,
        };
      },
      providesTags: [AdminApiTags.DRIVER_SINGLE],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: TLegacyDriver) => {
        return response;
      },
    }),
    // Uplaod Custom Asset Image
    uploadCustomAssetImage: builder.mutation<any, FormData>({
      query: (formData: FormData) => {
        return {
          url: API_ENDPOINTS.ADMINS.UPLOAD_CUSTOM_ASSET_ICON,
          method: API_METHODS.POST,
          body: formData,
          formData: true,
        };
      },
      invalidatesTags: [AdminApiTags.CUSTOM_ICON_CREATED],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: any) => {
        return response;
      },
    }),

    // get custom asset Image list
    getCustomAssetImageList: builder.query<any, void>({
      query: () => {
        return {
          url: API_ENDPOINTS.ADMINS.GET_CUSTOM_IMAGES,
          method: API_METHODS.GET,
          params: { display_mode: "custom_image" },
        };
      },
      providesTags: ["CUSTOM_ICON_CREATED" as any],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: any) => {
        return response;
      },
    }),

    // get standard asset icons list
    getStandardAssetIconsList: builder.query<any, void>({
      query: () => {
        return {
          url: API_ENDPOINTS.ADMINS.GET_STANDARD_ICONS,
          method: API_METHODS.GET,
          params: { display_mode: "standard_icon" },
        };
      },
      providesTags: [AdminApiTags.CUSTOM_ICON_CREATED],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: any) => {
        return response;
      },
    }),
    //edit organization driver
    editOrganizationDriver: builder.mutation<
      OrganizationDriver,
      EditOrganizationDriverPayload
    >({
      query: (body: EditOrganizationDriverPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.EDIT_ORGANIZATION_DRIVER,
          method: API_METHODS.PATCH,
          body: body.data,
        };
      },
      invalidatesTags: [
        AdminApiTags.DRIVER_MODIFIED,
        AdminApiTags.DRIVER_SINGLE,
      ],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: OrganizationDriver) => {
        return response;
      },
    }),

    //edit organization driver image
    editOrganizationDriverImage: builder.mutation<any, FormData>({
      query: (formData: FormData) => {
        return {
          url: API_ENDPOINTS.ADMINS.EDIT_ORGANIZATION_DRIVER_IMAGE,
          method: API_METHODS.PATCH,
          body: formData,
          formData: true,
        };
      },
      invalidatesTags: [
        AdminApiTags.DRIVER_MODIFIED,
        AdminApiTags.DRIVER_SINGLE,
      ],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: any) => {
        return response;
      },
    }),

    //delete organization driver
    deleteSingleDriver: builder.mutation<
      void,
      LegacyDeleteOrganizationDriverPayload
    >({
      query: (params: LegacyDeleteOrganizationDriverPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.DELETE_ORGANIZATION_DRIVER(
            params.account_id,
            params.driver_id
          ),
          // params: { organization_id },
          method: API_METHODS.DELETE,
        };
      },
      invalidatesTags: [AdminApiTags.DRIVER_DELETED],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: void) => {
        return response;
      },
    }),

    orgGHListGroups: builder.query<
      ListingResponse<OrgGHGroup[]>,
      GHListGroupsPayload
    >({
      query: (params: GHListGroupsPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.ORG_GH_LIST_GROUPS,
          params,
          method: API_METHODS.GET,
        };
      },
      providesTags: [
        AdminApiTags.GH_GROUP_CREATED,
        AdminApiTags.GH_GROUP_MODIFIED,
        AdminApiTags.GH_GROUP_DELETED,
      ],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: ListingResponse<OrgGHGroup[]>) => {
        return response;
      },
    }),

    createOrgGHGroup: builder.mutation<OrgGHGroup, OrgGHCreateGroupPayload>({
      query: (params: OrgGHCreateGroupPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.ORG_GH_CREATE_GROUP,
          method: API_METHODS.POST,
          body: params,
        };
      },
      invalidatesTags: [AdminApiTags.GH_GROUP_CREATED],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: OrgGHGroup) => {
        return response;
      },
    }),

    modifyOrgGHGroup: builder.mutation<OrgGHGroup, OrgGHModifyGroupPayload>({
      query: ({ groupid, ...restParams }: OrgGHModifyGroupPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.ORG_GH_UPDATE_DELETE_GROUP(groupid),
          method: API_METHODS.PATCH,
          body: restParams,
        };
      },
      invalidatesTags: [AdminApiTags.GH_GROUP_MODIFIED],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: OrgGHGroup) => {
        return response;
      },
    }),

    deleteOrgGHGroup: builder.mutation<any, OrgGHDeleteGroupPayload>({
      query: (params: OrgGHDeleteGroupPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.ORG_GH_UPDATE_DELETE_GROUP(params.groupid),
          method: API_METHODS.DELETE,
        };
      },
      invalidatesTags: [AdminApiTags.GH_GROUP_DELETED],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: any) => {
        return response;
      },
    }),

    // organization groups
    organizationGroups: builder.query<
      ListingResponse<TLegacyGroup[]>,
      LegacyGroupEntityListingPayload
    >({
      query: ({
        search_fields,
        ...params
      }: LegacyGroupEntityListingPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.ORGANIZATION_GROUPS,
          params: {
            ...params,
            ...getSearchFieldsParams(
              search_fields,
              APP_CONFIG.ENTITY_SEARCH_FIELDS.GROUP_LISTING
            ),
          },
          method: API_METHODS.GET,
        };
      },
      // keepUnusedDataFor: 60 * 5, // 5 minutes
      providesTags: [
        AdminApiTags.GROUP_CREATED,
        AdminApiTags.GROUP_MODIFIED,
        AdminApiTags.GROUP_DELETED,
        AdminApiTags.VEHICLE_MODIFIED,
      ],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: ListingResponse<TLegacyGroup[]>) => {
        return response;
      },
    }),

    // create organization group
    createOrganizationGroup: builder.mutation<
      TLegacyGroup,
      CreateOrganizationGroupPayload
    >({
      query: (params: CreateOrganizationGroupPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.ORGANIZATION_GROUPS,
          method: API_METHODS.POST,
          body: params,
        };
      },
      invalidatesTags: [AdminApiTags.GROUP_CREATED],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: TLegacyGroup) => {
        return response;
      },
    }),

    // single organization group
    singleOrganizationGroup: builder.query<
      ListingResponse<TLegacyGroup[]>,
      SingleOrganizationGroupPayload
    >({
      query: (params: SingleOrganizationGroupPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.ORGANIZATION_GROUPS,
          params,
          method: API_METHODS.GET,
        };
      },
      providesTags: [AdminApiTags.GROUP_SINGLE, AdminApiTags.VEHICLE_MODIFIED],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: ListingResponse<TLegacyGroup[]>) => {
        return response;
      },
    }),

    // get vehicles under single group
    singleOrganizationGroupVehicles: builder.query<
      TLegacyVehicle[],
      SingleOrganizationGroupPayload
    >({
      query: (params: SingleOrganizationGroupPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.SINGLE_ORGANIZATION_GROUP_VEHICLES(
            params.accountid,
            params.groupid
          ),
          params,
          method: API_METHODS.GET,
        };
      },
      providesTags: [AdminApiTags.GROUP_SINGLE, AdminApiTags.VEHICLE_MODIFIED],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: TLegacyVehicle[]) => {
        return response;
      },
    }),

    //edit organization group
    editOrganizationGroup: builder.mutation<
      OrganizationGroup,
      EditOrganizationGroupPayload
    >({
      query: (params: EditOrganizationGroupPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.EDIT_ORGANIZATION_GROUP,
          method: API_METHODS.PATCH,
          body: params.data,
        };
      },
      invalidatesTags: [
        AdminApiTags.GROUP_MODIFIED,
        AdminApiTags.GROUP_SINGLE,
        AdminApiTags.VEHICLE_SINGLE,
        AdminApiTags.VEHICLE_MODIFIED,
        // added following to let hierarchy refresh with new update
        // AdminApiTags.GH_GROUP_MODIFIED // disabled as vehicles count update is what needs to be reflected and is being handled by invalidating VEHICLE_MODIFIED in editOrganizationGroupVehicles
      ],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: OrganizationGroup) => {
        return response;
      },
    }),

    // edit organization group vehicles
    editOrganizationGroupVehicles: builder.mutation<
      OrganizationGroup,
      EditOrganizationGroupVehiclesPayload
    >({
      query: (params: EditOrganizationGroupVehiclesPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.EDIT_ORGANIZATION_GROUP_VEHICLES,
          method: API_METHODS.POST,
          body: params.data,
        };
      },
      invalidatesTags: [
        AdminApiTags.GROUP_MODIFIED,
        AdminApiTags.GROUP_SINGLE,
        AdminApiTags.VEHICLE_SINGLE,
        AdminApiTags.VEHICLE_MODIFIED,
        // added following to let hierarchy refresh with new update
        AdminApiTags.GH_GROUP_MODIFIED,
      ],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: OrganizationGroup) => {
        return response;
      },
    }),

    // fetch users under the selected group
    singleOrganizationGroupUsers: builder.query<
      TLegacyUser[],
      SingleOrganizationGroupPayload
    >({
      query: (params: SingleOrganizationGroupPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.SINGLE_ORGANIZATION_GROUP_USERS(
            params.accountid,
            params.groupid
          ),
          params,
          method: API_METHODS.GET,
        };
      },
      providesTags: [AdminApiTags.GROUP_SINGLE, AdminApiTags.USER_MODIFIED],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: TLegacyUser[]) => {
        return response;
      },
    }),

    // edit organization group users (add/remove sync)
    editOrganizationGroupUsers: builder.mutation<
      OrganizationGroup,
      EditOrganizationGroupUsersPayload
    >({
      query: (params: EditOrganizationGroupUsersPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.EDIT_ORGANIZATION_GROUP_USERS,
          method: API_METHODS.POST,
          body: params.data,
        };
      },
      invalidatesTags: [
        AdminApiTags.GROUP_MODIFIED,
        AdminApiTags.GROUP_SINGLE,
        AdminApiTags.USER_MODIFIED,
        // let hierarchy refresh with new update (user counts)
        AdminApiTags.GH_GROUP_MODIFIED,
      ],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: OrganizationGroup) => {
        return response;
      },
    }),

    //delete organization group
    deleteSingleGroup: builder.mutation<void, SingleOrganizationGroupPayload>({
      query: (params: SingleOrganizationGroupPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.DELETE_ORGANIZATION_GROUP(
            params.accountid,
            params.groupid
          ),
          params,
          method: API_METHODS.DELETE,
        };
      },
      invalidatesTags: [
        AdminApiTags.GROUP_DELETED,
        AdminApiTags.GROUP_SINGLE,
        AdminApiTags.VEHICLE_MODIFIED,
      ], // added GROUP_SINGLE, VEHICLE_MODIFIED to prevent stale data when re-creating group with same name
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: void) => {
        return response;
      },
    }),

    //edit organization account
    editOrganizationAccount: builder.mutation<
      LegacyOrganizationAccount,
      EditOrganizationAccountPayload
    >({
      // TODO: change to legacy incomplete
      query: ({ account_id, data }) => {
        return {
          url: API_ENDPOINTS.ADMINS.EDIT_ORGANIZATION_ACCOUNT(account_id),
          method: API_METHODS.PATCH,
          body: data,
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: AdminApiTags.ENTERPRISE, id: arg.account_id },
      ],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: LegacyOrganizationAccount) => {
        // TODO: change to legacy incomplete
        return response;
      },
    }),

    //organization fleettag(All)
    organizationFleettags: builder.query<
      ListingResponse<TLegacyFleettag[]>,
      LegacyFleettagEntityListingPayload
    >({
      query: (params: LegacyFleettagEntityListingPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.ORGANIZATION_FLEETTAGS,
          params,
          method: API_METHODS.GET,
        };
      },
      providesTags: [
        AdminApiTags.FLEETTAG_CREATED,
        AdminApiTags.FLEETTAG_MODIFIED,
        AdminApiTags.FLEETTAG_DELETED,
      ],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: ListingResponse<TLegacyFleettag[]>) => {
        return response;
      },
    }),

    // create organization fleettag
    createOrganizationFleettag: builder.mutation<
      TLegacyFleettag,
      CreateOrganizationFleettagPayload
    >({
      query: (body: CreateOrganizationFleettagPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.ORGANIZATION_FLEETTAGS,
          method: API_METHODS.POST,
          body,
        };
      },
      invalidatesTags: [AdminApiTags.FLEETTAG_CREATED],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: TLegacyFleettag) => {
        return response;
      },
    }),

    // single organization fleettag
    singleOrganizationFleettag: builder.query<
      TLegacyFleettag,
      SingleOrganizationFleettagPayload
    >({
      query: (params: SingleOrganizationFleettagPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.SINGLE_ORGANIZATION_FLEETTAG,
          params,
          method: API_METHODS.GET,
        };
      },
      providesTags: [AdminApiTags.FLEETTAG_SINGLE],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: TLegacyFleettag) => {
        return response;
      },
    }),

    //edit organization fleettag
    editOrganizationFleettag: builder.mutation<
      TLegacyFleettag,
      EditOrganizationFleettagPayload
    >({
      query: (body: EditOrganizationFleettagPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.EDIT_ORGANIZATION_FLEETTAG,
          method: API_METHODS.PATCH,
          body: body.data,
        };
      },
      invalidatesTags: [
        AdminApiTags.FLEETTAG_MODIFIED,
        AdminApiTags.FLEETTAG_SINGLE,
      ],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: TLegacyFleettag) => {
        return response;
      },
    }),

    //delete organization fleettag
    deleteSingleFleettag: builder.mutation<
      void,
      LegacyDeleteOrganizationFleettagPayload
    >({
      query: (params: LegacyDeleteOrganizationFleettagPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.DELETE_ORGANIZATION_FLEETTAG(
            params.account_id,
            params.entity_id
          ),
          // params: { organization_id },
          method: API_METHODS.DELETE,
        };
      },
      invalidatesTags: [AdminApiTags.FLEETTAG_DELETED],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: void) => {
        return response;
      },
    }),

    //organization geozone
    organizationGeozones: builder.query<
      ListingResponse<TLegacyGeozone[]>,
      LegacyGeozoneEntityListingPayload
    >({
      query: ({
        search_fields,
        ...params
      }: LegacyGeozoneEntityListingPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.ORGANIZATION_GEOZONES,
          params: {
            ...params,
            ...getSearchFieldsParams(
              search_fields,
              APP_CONFIG.ENTITY_SEARCH_FIELDS.GEOZONE_LISTING
            ),
          },
          method: API_METHODS.GET,
        };
      },
      providesTags: [
        AdminApiTags.GEOZONE_CREATED,
        AdminApiTags.GEOZONE_MODIFIED,
        AdminApiTags.GEOZONE_DELETED,
      ],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: ListingResponse<TLegacyGeozone[]>) => {
        return response;
      },
    }),

    // create organization geozone
    createOrganizationGeozone: builder.mutation<
      TLegacyGeozone,
      CreateOrganizationGeozonePayload
    >({
      query: (params: CreateOrganizationGeozonePayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.ORGANIZATION_GEOZONES,
          method: API_METHODS.POST,
          body: params,
        };
      },
      invalidatesTags: [AdminApiTags.GEOZONE_CREATED],
      transformErrorResponse(baseQueryReturnValue) {
        // disabled error handling here, instead it is now handled in `createNewGeozone.tsx` file
        // handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: TLegacyGeozone) => {
        return response;
      },
    }),

    // single organization geozone
    singleOrganizationGeozone: builder.query<
      TLegacyGeozone,
      SingleOrganizationGeozonePayload
    >({
      query: (params: SingleOrganizationGeozonePayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.SINGLE_ORGANIZATION_GEOZONE,
          params,
          method: API_METHODS.GET,
        };
      },
      providesTags: [AdminApiTags.GEOZONE_SINGLE],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: TLegacyGeozone) => {
        return response;
      },
    }),

    //edit organization geozone
    editOrganizationGeozone: builder.mutation<
      TLegacyGeozone,
      EditOrganizationGeozonePayload
    >({
      query: (params: EditOrganizationGeozonePayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.EDIT_ORGANIZATION_GEOZONE,
          method: API_METHODS.PATCH,
          body: params,
        };
      },
      invalidatesTags: [
        AdminApiTags.GEOZONE_MODIFIED,
        AdminApiTags.GEOZONE_SINGLE,
      ],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: TLegacyGeozone) => {
        return response;
      },
    }),

    //delete organization geozone
    deleteSingleGeozone: builder.mutation<
      void,
      SingleOrganizationGeozonePayload
    >({
      query: ({ account_id, geozone_id }) => {
        return {
          url: API_ENDPOINTS.ADMINS.DELETE_ORGANIZATION_GEOZONE(
            account_id,
            geozone_id
          ),
          method: API_METHODS.DELETE,
        };
      },
      invalidatesTags: [AdminApiTags.GEOZONE_DELETED],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: void) => {
        return response;
      },
    }),

    // organization geozone category
    organizationGeozoneCategories: builder.query<
      ListingResponse<TLegacyGeozoneCategory[]>,
      LegacyGeozoneCategoryEntityListingPayload
    >({
      query: ({
        search_fields,
        ...params
      }: LegacyGeozoneCategoryEntityListingPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.ORGANIZATION_GEOZONE_CATEGORIES,
          params: {
            ...params,
            ...getSearchFieldsParams(
              search_fields,
              APP_CONFIG.ENTITY_SEARCH_FIELDS.GEOZONE_CATEGORY_LISTING
            ),
          },
          method: API_METHODS.GET,
        };
      },
      providesTags: [
        AdminApiTags.GEOZONE_CATEGORY_CREATED,
        AdminApiTags.GEOZONE_CATEGORY_MODIFIED,
        AdminApiTags.GEOZONE_CATEGORY_DELETED,
      ],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (
        response: ListingResponse<TLegacyGeozoneCategory[]>
      ) => {
        return response;
      },
    }),

    // single organization geozone category
    singleOrganizationGeozoneCategory: builder.query<
      TLegacyGeozoneCategory,
      SingleOrganizationGeozoneCategoryPayload
    >({
      query: ({ categoryid }: SingleOrganizationGeozoneCategoryPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.SINGLE_ORGANIZATION_GEOZONE_CATEGORY(
            categoryid
          ),
          method: API_METHODS.GET,
        };
      },
      providesTags: [AdminApiTags.GEOZONE_CATEGORY_SINGLE],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: TLegacyGeozoneCategory) => {
        return response;
      },
    }),

    // create organization geozone category
    createOrganizationGeozoneCategory: builder.mutation<
      TLegacyGeozoneCategory,
      CreateGeozoneCategoryPayload
    >({
      query: (params: CreateGeozoneCategoryPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.CREATE_ORGANIZATION_GEOZONE_CATEGORY,
          method: API_METHODS.POST,
          body: params,
        };
      },
      invalidatesTags: [AdminApiTags.GEOZONE_CATEGORY_CREATED],
      transformErrorResponse(baseQueryReturnValue) {
        // disabled error handling here, instead it is now handled in `createNewGeozoneCategory.tsx` file
        // handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: TLegacyGeozoneCategory) => {
        return response;
      },
    }),

    // edit organization geozone category
    editOrganizationGeozoneCategory: builder.mutation<
      TLegacyGeozoneCategory,
      EditGeozoneCategoryPayload
    >({
      query: (params: EditGeozoneCategoryPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.EDIT_ORGANIZATION_GEOZONE_CATEGORY,
          method: API_METHODS.PATCH,
          body: params,
        };
      },
      invalidatesTags: [
        AdminApiTags.GEOZONE_CATEGORY_MODIFIED,
        AdminApiTags.GEOZONE_CATEGORY_SINGLE,
      ],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: TLegacyGeozoneCategory) => {
        return response;
      },
    }),

    // delete organization geozone category
    deleteOrganizationGeozoneCategory: builder.mutation<
      void,
      GeozoneCategoryBasePayload
    >({
      query: ({ categoryid }) => {
        return {
          url: API_ENDPOINTS.ADMINS.DELETE_ORGANIZATION_GEOZONE_CATEGORIES(
            categoryid
          ),
          method: API_METHODS.DELETE,
        };
      },
      invalidatesTags: [AdminApiTags.GEOZONE_CATEGORY_DELETED],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: void) => {
        return response;
      },
    }),

    // assign geozone to geozone category
    assignGeozoneToGeozoneCategory: builder.mutation<
      TLegacyGeozoneCategory,
      AssignGeozoneToGeozoneCategoryPayload
    >({
      query: (params: AssignGeozoneToGeozoneCategoryPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.ASSIGN_GEOZONE_TO_GEOZONE_CATEGORY,
          method: API_METHODS.POST,
          body: params,
        };
      },
      invalidatesTags: [AdminApiTags.GEOZONE_CATEGORY_MODIFIED],
      transformErrorResponse(baseQueryReturnValue) {
        // disabled error handling here, instead it is now handled in `createNewGeozone.tsx` file
        // handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: TLegacyGeozoneCategory) => {
        return response;
      },
    }),

    // assign user to geozone category
    assignUserToGeozoneCategory: builder.mutation<
      TLegacyGeozoneCategory,
      AssignUserToGeozoneCategoryPayload
    >({
      query: (params: AssignUserToGeozoneCategoryPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.ASSIGN_USER_TO_GEOZONE_CATEGORY,
          method: API_METHODS.POST,
          body: params,
        };
      },
      invalidatesTags: [AdminApiTags.GEOZONE_CATEGORY_MODIFIED],
      transformErrorResponse(baseQueryReturnValue) {
        // disabled error handling here, instead it is now handled in `createNewGeozone.tsx` file
        // handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: TLegacyGeozoneCategory) => {
        return response;
      },
    }),

    // organization vehicle alert
    organizationVehicleAlerts: builder.query<
      ListingResponse<TLegacyAlert[]>,
      LegacyOrganizationAlertListingPayload
    >({
      query: (params: LegacyOrganizationAlertListingPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.ORGANIZATION_VEHICLE_ALERTS(
            params.accountid ?? null
          ),
          params: {
            ...params,
            ...getSearchFieldsParams(params.search_fields, [
              ...getAccountConfiguredAssetSearchFields().map((field) =>
                field === "description" ? "ft_description" : field
              ),
              ...APP_CONFIG.ENTITY_SEARCH_FIELDS.VEHICLE_ALERT_LISTING,
            ]),
          },
          method: API_METHODS.GET,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: ListingResponse<TLegacyAlert[]>) => {
        return response;
      },
    }),

    // single organization vehicle alert
    singleOrganizationVehicleAlert: builder.query<
      ListingResponse<TLegacySingleAlert[]>,
      SingleOrganizationVehicleAlertPayload
    >({
      query: (params: SingleOrganizationVehicleAlertPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.SINGLE_ORGANIZATION_VEHICLE_ALERT(
            params.accountid ?? null,
            params.deviceid ?? null
          ),
          params,
          method: API_METHODS.GET,
        };
      },
      providesTags: [AdminApiTags.VEHICLE_ALERT_SINGLE],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: ListingResponse<TLegacySingleAlert[]>) => {
        return response;
      },
    }),

    // Send text to selected users
    sendMessage: builder.mutation<any, { phone: string; body: string }>({
      query: ({ phone, body }) => {
        return {
          url: API_ENDPOINTS.ADMINS.SEND_MESSAGE,
          method: API_METHODS.POST,
          body: { phone, body },
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: void) => {
        return response;
      },
    }),

    // get driver assigned to vehicle
    getAssignedDriver: builder.query<
      TLegacyDriver,
      { account_id: string; device_id: string }
    >({
      query: ({ account_id, device_id }) => {
        return {
          url: API_ENDPOINTS.ADMINS.GET_ASSIGNED_DRIVER(account_id, device_id),
          method: API_METHODS.GET,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        // handleAuthErrorCode(baseQueryReturnValue); // hide error message
        return baseQueryReturnValue;
      },
      transformResponse: (response: TLegacyDriver) => {
        return response;
      },
    }),

    // Get event report for vehicle
    getDetailedReport: builder.query<any, LegacyReportListingPayload>({
      query: (params: LegacyReportListingPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.LEGACY_REPORTS(params),
          method: API_METHODS.GET,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        // handleAuthErrorCode(baseQueryReturnValue); // hide error message
        return baseQueryReturnValue;
      },
      transformResponse: (response: any) => {
        return response;
      },
    }),

    // Get Vehicle camera details
    getVehicleCameraDetails: builder.query<
      any,
      { account_id: string; device_id: string }
    >({
      query: ({ account_id, device_id }) => {
        return {
          url: API_ENDPOINTS.ADMINS.ASSET_CAMERA_DETAILS,
          method: API_METHODS.GET,
          params: { account_id, device_id },
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        return baseQueryReturnValue;
      },
      transformResponse: (response: any) => {
        return response;
      },
    }),

    // Get Vehicle camera recording
    getEventCameraRecording: builder.query<
      TRecordingDetails,
      {
        device_id: string;
        timestamp: string | number;
        event_id: string | number;
      }
    >({
      query: ({ device_id, timestamp, event_id }) => {
        return {
          url: API_ENDPOINTS.ADMINS.ASSET_CAMERA_RECORDING(
            device_id,
            timestamp,
            event_id
          ),
          method: API_METHODS.GET,
          params: { time: Date.now() /* to avoid caching */ },
        };
      },
      providesTags: [AdminApiTags.ASSET_EVENT_VIDEO_RECORDING],
      transformErrorResponse(baseQueryReturnValue) {
        return baseQueryReturnValue;
      },
      transformResponse: (response: TRecordingDetails) => {
        return response;
      },
    }),

    // Get Vehicle camera recording
    cameraRequestVideo: builder.mutation<
      any,
      {
        device_id: string;
        timestamp: string | number;
        event_id: string | number;
      }
    >({
      query: ({ device_id, timestamp, event_id }) => {
        return {
          url: API_ENDPOINTS.ADMINS.ASSET_REQUEST_VIDEO(
            device_id,
            timestamp,
            event_id
          ),
          method: API_METHODS.POST,
        };
      },
      invalidatesTags: [AdminApiTags.ASSET_EVENT_VIDEO_RECORDING],
      transformErrorResponse(baseQueryReturnValue) {
        return baseQueryReturnValue;
      },
      transformResponse: (response: any) => {
        return response;
      },
    }),

    // get vision max token
    visionMaxGetToken: builder.query<VisionMaxGetTokenResponse, any>({
      query: () => {
        return {
          url: API_ENDPOINTS.ADMINS.VISIONMAX_GET_TOKEN,
          method: API_METHODS.GET,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: VisionMaxGetTokenResponse) => {
        return response;
      },
    }),

    // manage rule alert

    // create organization rule alert
    createOrganizationRuleAlertList: builder.mutation<
      LegacyCreateOrganizationRuleResponse,
      LegacyCreateOrganizationRulePayload
    >({
      query: (params: LegacyCreateOrganizationRulePayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.ORGANIZATION_RULE_ALERT,
          method: API_METHODS.POST,
          body: params,
        };
      },
      invalidatesTags: [AdminApiTags.MANAGE_RULE_ALERT_CREATED],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: LegacyCreateOrganizationRuleResponse) => {
        return response;
      },
    }),

    // get organization rule alert
    organizationRuleAlertList: builder.query<
      ListingResponse<TLegacyRule[]>,
      LegacyRuleEntityListingPayload
    >({
      query: ({ search_fields, ...params }: LegacyRuleEntityListingPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.ORGANIZATION_RULE_ALERT,
          params: {
            ...params,
            ...getSearchFieldsParams(
              search_fields,
              APP_CONFIG.ENTITY_SEARCH_FIELDS.RULE_LISTING
            ),
          },
          method: API_METHODS.GET,
        };
      },
      providesTags: [
        AdminApiTags.MANAGE_RULE_ALERT_CREATED,
        AdminApiTags.MANAGE_RULE_ALERT_MODIFIED,
        AdminApiTags.MANAGE_RULE_ALERT_DELETED,
      ],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: ListingResponse<TLegacyRule[]>) => {
        return response;
      },
    }),

    // single organization rule alert details
    singleOrganizationRuleAlert: builder.query<
      LegacyRuleWithAccount,
      SingleOrganizationRulePayload
    >({
      query: ({ ruleid }) => {
        return {
          url: API_ENDPOINTS.ADMINS.SINGLE_ORGANIZATION_RULE_ALERT(ruleid),
          method: API_METHODS.GET,
        };
      },
      providesTags: [AdminApiTags.MANAGE_RULE_ALERT_SINGLE],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: LegacyRuleWithAccount) => {
        return response;
      },
    }),

    // edit organization rule
    editOrganizationRuleAlert: builder.mutation<
      LegacyRuleWithAccount,
      Partial<TLegacyEditOrganizationRulePayload>
    >({
      query: (params: Partial<TLegacyEditOrganizationRulePayload>) => {
        return {
          url: API_ENDPOINTS.ADMINS.EDIT_ORGANIZATION_RULE(params?.ruleid),
          method: API_METHODS.PATCH,
          body: params,
        };
      },
      invalidatesTags: [
        AdminApiTags.MANAGE_RULE_ALERT_MODIFIED,
        AdminApiTags.MANAGE_RULE_ALERT_SINGLE,
      ],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: LegacyRuleWithAccount) => {
        return response;
      },
    }),

    // delete organization rule
    deleteOrganiaztionRule: builder.mutation<
      void,
      LegacyDeleteOrganizationRulePayload
    >({
      query: ({ accountid, ruleid }: LegacyDeleteOrganizationRulePayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.DELETE_ORGANIZATION_RULE(accountid, ruleid),
          method: API_METHODS.DELETE,
        };
      },
      invalidatesTags: [AdminApiTags.MANAGE_RULE_ALERT_DELETED],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: void) => {
        return response;
      },
    }),

    // get system rule
    getSystemRule: builder.query<
      ListingResponse<TLegacyRule[]>,
      LegacyRuleEntityListingPayload
    >({
      query: (params: LegacyRuleEntityListingPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.SYSTEM_RULE_CRITERIA,
          params,
          method: API_METHODS.GET,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: ListingResponse<TLegacyRule[]>) => {
        return response;
      },
    }),

    // get notification variables
    getNotificationVariableList: builder.query<
      ListingResponse<NotificationVariableResponse[]>,
      LegacyRuleEntityListingPayload
    >({
      query: (params: LegacyRuleEntityListingPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.NOTIFICATION_VARIABLE_LIST,
          params,
          method: API_METHODS.GET,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (
        response: ListingResponse<NotificationVariableResponse[]>
      ) => {
        return response;
      },
    }),

    // Get document list
    organisationDocuments: builder.query<
      ListingResponse<TDocument[]>,
      LegacyDocumentEntityListingPayload
    >({
      query: ({
        search_fields,
        ...params
      }: LegacyDocumentEntityListingPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.ORGANIZATION_DOCUMENTS,
          method: API_METHODS.GET,
          params: {
            ...params,
            ...getSearchFieldsParams(
              search_fields,
              APP_CONFIG.ENTITY_SEARCH_FIELDS.DOCUMENT_LISTING
            ),
          },
        };
      },
      providesTags: [
        AdminApiTags.DOCUMENTS_CREATED,
        AdminApiTags.DOCUMENT_MODIFIED,
      ],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: ListingResponse<TDocument[]>) => {
        return response;
      },
    }),
    // Upload new document
    uploadOrganisationDocument: builder.mutation<TDocument, FormData>({
      query: (formData: FormData) => {
        return {
          url: API_ENDPOINTS.ADMINS.ORGANIZATION_DOCUMENTS,
          method: API_METHODS.POST,
          body: formData,
          formData: true,
        };
      },
      invalidatesTags: [AdminApiTags.DOCUMENTS_CREATED],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: TDocument) => {
        return response;
      },
    }),
    // Get specific document by id
    organisationSingleDocument: builder.query<
      TDocument,
      TSingleDocumentPayload
    >({
      query: (params: TSingleDocumentPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.ORGANIZATION_SINGLE_DOCUMENT(params.id),
          method: API_METHODS.GET,
          params,
        };
      },
      providesTags: [AdminApiTags.DOCUMENT_MODIFIED],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: TDocument) => {
        return response;
      },
    }),
    // Edit document (Set Active/Inactive)
    editOrganisationDocument: builder.mutation<any, Partial<TDocument>>({
      query: (params: Partial<TDocument>) => {
        return {
          url: API_ENDPOINTS.ADMINS.ORGANIZATION_SINGLE_DOCUMENT(
            params.id?.toString() || ""
          ),
          method: API_METHODS.PATCH,
          body: params,
        };
      },
      invalidatesTags: [AdminApiTags.DOCUMENT_MODIFIED],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: TDocument) => {
        return response;
      },
    }),

    // Add users to role
    addUsersToRole: builder.mutation<
      TAddUsersToRoleResponse,
      TAddUserToRolePayload
    >({
      query: (data: TAddUserToRolePayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.ADD_USERS_TO_ROLE,
          method: API_METHODS.POST,
          body: data,
        };
      },
      invalidatesTags: [AdminApiTags.USER_MODIFIED, AdminApiTags.ROLE_MODIFIED],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: TAddUsersToRoleResponse) => {
        return response;
      },
    }),

    // global search
    globalSearch: builder.query<GlobalSearchResponse, GlobalSearchPayload>({
      query: (params: GlobalSearchPayload) => {
        return {
          url: API_ENDPOINTS.GLOBAL_SEARCH.SEARCH,
          method: API_METHODS.GET,
          params,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: GlobalSearchResponse) => {
        return response;
      },
    }),

    // heatmap - get config
    heatmapConfig: builder.query<THeatmapConfigListingResponse, void>({
      query: () => {
        return {
          url: API_ENDPOINTS.HEATMAP.CONFIG,
          method: API_METHODS.GET,
        };
      },
      providesTags: [AdminApiTags.HEATMAP_CONFIG],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: THeatmapConfigListingResponse) => {
        return response;
      },
    }),

    // heatmap - update config
    updateHeatmapConfig: builder.mutation<
      TUpdateHeatmapConfigResponse,
      TUpdateHeatmapConfigPayload
    >({
      query: (data: TUpdateHeatmapConfigPayload) => {
        return {
          url: API_ENDPOINTS.HEATMAP.CONFIG,
          method: API_METHODS.POST,
          body: data,
        };
      },
      invalidatesTags: [AdminApiTags.HEATMAP_CONFIG],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: TUpdateHeatmapConfigResponse) => {
        return response;
      },
    }),

    // heatmap - get events
    heatmapEvents: builder.query<THeatmapEventsResponse, THeatmapEventsPayload>(
      {
        query: (params: THeatmapEventsPayload) => {
          return {
            url: API_ENDPOINTS.HEATMAP.EVENTS,
            method: API_METHODS.POST,
            body: params,
          };
        },
        transformErrorResponse(baseQueryReturnValue) {
          handleAuthErrorCode(baseQueryReturnValue);
          return baseQueryReturnValue;
        },
        transformResponse: (response: THeatmapEventsResponse) => {
          return response;
        },
      }
    ),
    // Get white label config
    getWhiteLabelConfigList: builder.query<
      ListingResponse<WhiteLabelConfig[]>,
      WhiteLabelParam
    >({
      query: (params: WhiteLabelParam) => {
        return {
          url: API_ENDPOINTS.ADMINS.WHITE_LABEL_CONFIG,
          method: API_METHODS.GET,
          params,
        };
      },
      providesTags: [
        AdminApiTags.WHITE_LABEL_CREATED,
        AdminApiTags.WHITE_LABEL_MODIFIED,
      ],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: ListingResponse<WhiteLabelConfig[]>) => {
        return response;
      },
    }),
    getWhiteLabelConfig: builder.query<WhiteLabelConfig, string>({
      query: (id: string) => {
        return {
          url: `${API_ENDPOINTS.ADMINS.WHITE_LABEL_CONFIG}${id}/`,
          method: API_METHODS.GET,
        };
      },
      providesTags: [
        AdminApiTags.WHITE_LABEL_CREATED,
        AdminApiTags.WHITE_LABEL_MODIFIED,
      ],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: WhiteLabelConfig) => {
        return response;
      },
    }),
    // Create white label config
    createWhiteLabelConfig: builder.mutation<
      WhiteLabelConfig,
      Partial<WhiteLabelConfig>
    >({
      query: (data: Partial<WhiteLabelConfig>) => {
        const formData = new FormData();
        Object.entries(data).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            formData.append(key, value as any); // cast to `any` to handle strings/files
          }
        });
        return {
          url: API_ENDPOINTS.ADMINS.WHITE_LABEL_CONFIG,
          method: API_METHODS.POST,
          body: formData,
        };
      },
      invalidatesTags: [AdminApiTags.WHITE_LABEL_CREATED],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: WhiteLabelConfig) => {
        return response;
      },
    }),
    // Update White Label config
    updateWhiteLabelConfig: builder.mutation<
      WhiteLabelConfig,
      { id: string | number; data: Partial<WhiteLabelConfig> }
    >({
      query: ({ id, data }) => {
        const formData = new FormData();
        Object.entries(data).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            formData.append(key, value as any); // cast to `any` to handle strings/files
          }
        });
        return {
          url: `${API_ENDPOINTS.ADMINS.WHITE_LABEL_CONFIG}${id}/`,
          method: API_METHODS.PATCH,
          body: formData,
        };
      },
      invalidatesTags: [AdminApiTags.WHITE_LABEL_MODIFIED],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: WhiteLabelConfig) => {
        return response;
      },
    }),
    deleteWhiteLabelConfig: builder.mutation<WhiteLabelConfig, string | number>(
      {
        query: (id: string | number) => {
          return {
            url: `${API_ENDPOINTS.ADMINS.WHITE_LABEL_CONFIG}${id}/`,
            method: API_METHODS.DELETE,
          };
        },
        invalidatesTags: [AdminApiTags.WHITE_LABEL_MODIFIED],
        transformErrorResponse(baseQueryReturnValue) {
          handleAuthErrorCode(baseQueryReturnValue);
          return baseQueryReturnValue;
        },
        transformResponse: (response: WhiteLabelConfig) => {
          return response;
        },
      }
    ),

    // generic status codes
    genericStatusCodes: builder.query<
      ListingResponse<TGenericStatusCode[]>,
      BasicEntityListingPayload
    >({
      query: (params: BasicEntityListingPayload) => {
        return {
          url: API_ENDPOINTS.GENERIC_STATUS_CODE,
          method: API_METHODS.GET,
          params,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: ListingResponse<TGenericStatusCode[]>) => {
        return response;
      },
    }),

    // account event colours (FT1 StatusCodeInfo)
    accountStatusCodeColors: builder.query<TAccountStatusCodeColor[], void>({
      query: () => {
        return {
          url: API_ENDPOINTS.ACCOUNT_STATUS_CODE_COLORS,
          method: API_METHODS.GET,
        };
      },
      providesTags: [AdminApiTags.STATUS_CODE_COLORS],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
    }),
    updateAccountStatusCodeColor: builder.mutation<
      unknown,
      TUpdateAccountStatusCodeColorPayload
    >({
      query: (data: TUpdateAccountStatusCodeColorPayload) => {
        return {
          url: API_ENDPOINTS.ACCOUNT_STATUS_CODE_COLORS,
          method: API_METHODS.PUT,
          body: data,
        };
      },
      invalidatesTags: [AdminApiTags.STATUS_CODE_COLORS],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
    }),
    resetAccountStatusCodeColor: builder.mutation<unknown, number>({
      query: (statusCode: number) => {
        return {
          url: API_ENDPOINTS.ACCOUNT_STATUS_CODE_COLORS,
          method: API_METHODS.DELETE,
          params: { statusCode },
        };
      },
      invalidatesTags: [AdminApiTags.STATUS_CODE_COLORS],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
    }),

    // crash track
    crashTrack: builder.query<
      ListingResponse<TCrashTrackRecord[]>,
      CrashTrackListingPayload
    >({
      query: ({ search_fields, ...params }: CrashTrackListingPayload) => {
        return {
          url: API_ENDPOINTS.CRASH_TRACK.LISTING,
          params: {
            ...params,
            ...getSearchFieldsParams(
              search_fields,
              APP_CONFIG.ENTITY_SEARCH_FIELDS.CRASH_TRACK_LISTING
            ),
          },
          method: API_METHODS.GET,
        };
      },
      // providesTags: [
      //   AdminApiTags.USER_CREATED,
      //   AdminApiTags.USER_MODIFIED,
      //   AdminApiTags.USER_DELETED,
      // ],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: ListingResponse<TCrashTrackRecord[]>) => {
        return response;
      },
    }),

    // crash track - asset info
    crashTrackAssetInfo: builder.query<
      CrashTrackAssetInfoResponse,
      CrashTrackTabDataPayload
    >({
      query: ({ assetId, timestamp }: CrashTrackTabDataPayload) => {
        return {
          url: API_ENDPOINTS.CRASH_TRACK.ASSET_INFO(assetId, timestamp),
          method: API_METHODS.GET,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: CrashTrackAssetInfoResponse) => {
        return response;
      },
    }),

    // crash track - incident info
    crashTrackIncidentInfo: builder.query<
      CrashTrackIncidentInfoResponse,
      CrashTrackTabDataPayload
    >({
      query: ({ assetId, timestamp }: CrashTrackTabDataPayload) => {
        return {
          url: API_ENDPOINTS.CRASH_TRACK.INCLIDENT_INFO(assetId, timestamp),
          method: API_METHODS.GET,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: CrashTrackIncidentInfoResponse) => {
        return response;
      },
    }),

    // crash track - chart data
    crashTrackChartData: builder.query<
      CrashTrackChartDataResponse,
      CrashTrackTabDataPayload
    >({
      query: ({ assetId, timestamp }: CrashTrackTabDataPayload) => {
        return {
          url: API_ENDPOINTS.CRASH_TRACK.CHARTS_DATA(assetId, timestamp),
          method: API_METHODS.GET,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: CrashTrackChartDataResponse) => {
        return response;
      },
    }),

    // crash track - event report
    crashTrackEventReport: builder.query<
      CrashTrackEventReportResponse,
      CrashTrackTabDataPayload
    >({
      query: ({ assetId, timestamp }: CrashTrackTabDataPayload) => {
        return {
          url: API_ENDPOINTS.CRASH_TRACK.EVENT_REPORT(assetId, timestamp),
          method: API_METHODS.GET,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: any) => {
        return response;
      },
    }),
  }),
});

export const {
  // FT 1.0 link
  useLazyGetFleetTrack1LinkQuery,

  // user admin
  useOrganizationRolesPermissionsQuery,
  useCreateOrganizationUserMutation,
  useResendInviteToOrgUserMutation,
  useOrganizationUsersQuery,
  useLazyOrganizationUsersQuery,
  useOrganizationUsersFilterableQuery,
  useSingleOrganizationUserQuery,
  useEditOrganizationUserMutation,
  useAccountFt1AccessPolicyQuery,
  useEditAccountFt1AccessPolicyMutation,
  useUserFt1AccessQuery,
  useEditUserFt1AccessMutation,
  useDeleteSingleUserMutation,

  // system roles
  useOrganizationSystemRolesQuery,

  // role admin
  useCreateOrganizationRoleMutation,
  useOrganizationRolesQuery,
  useLazyOrganizationRolesQuery,
  useCloneOrganizationRoleMutation,
  useSingleOrganizationRoleQuery,
  useEditOrganizationRoleMutation,
  useEditOrganizationUserACLMutation,
  useDeleteOrganiaztionRoleMutation,
  useAddUsersToRoleMutation,

  // role admin - ACL
  useOrganizationRoleACLQuery,
  useCreateModifyOrganizationRoleACLMutation,
  useOrganizationRoleCategoryListQuery,
  useOrganizationRoleCategoryACLListQuery,

  // vehicle admin
  useOrganizationVehiclesQuery,
  useLazyOrganizationVehiclesQuery,
  useOrganizationVehiclesFilterableQuery,
  useOrganizationVehiclesEventsQuery,
  useAllAssetLocationsQuery,
  useCreateOrganizationVehicleMutation,
  useSingleOrganizationVehicleQuery,
  useEditOrganizationVehicleMutation,
  useEditOrganizationVehicleImageMutation,
  useVehicleRecorderStatusQuery,
  useLazyVehicleRecorderMediaTokenQuery,
  useDeleteSingleVehicleMutation,
  //vehicle zip ties
  useVehicleZipTiesQuery,
  useDeleteVehicleZipTieMutation,
  usePatchVehicleZipTieMutation,
  usePostVehicleZipTieMutation,

  // vehicle map data
  useVehicleMapDataQuery,
  useLazyVehicleMapDataQuery,
  // multi vehicle map data
  useVehicleMapDataMultiQuery,
  useLazyVehicleMapDataMultiQuery,
  // multi vehicle map data - event count preflight
  useVehicleMapDataMultiEventCountQuery,
  useLazyVehicleMapDataMultiEventCountQuery,
  // vehicle decode vin
  useVehicleDecodeVinQuery,
  useLazyVehicleDecodeVinQuery,
  //uniqueId validation
  useValidateUniqueIdMutation,
  // driver admin
  useOrganizationDriversQuery,
  useLazyOrganizationDriversQuery,
  useCreateOrganizationDriverMutation,
  useSingleOrganizationDriverQuery,
  useEditOrganizationDriverMutation,
  useEditOrganizationDriverImageMutation,
  useDeleteSingleDriverMutation,

  // group hierarchy
  useOrgGHListGroupsQuery,
  useLazyOrgGHListGroupsQuery,
  useCreateOrgGHGroupMutation,
  useModifyOrgGHGroupMutation,
  useDeleteOrgGHGroupMutation,

  // group admin
  useOrganizationGroupsQuery,
  useLazyOrganizationGroupsQuery,
  useCreateOrganizationGroupMutation,
  useSingleOrganizationGroupQuery,
  useLazySingleOrganizationGroupQuery,
  useSingleOrganizationGroupVehiclesQuery,
  useSingleOrganizationGroupUsersQuery,
  useEditOrganizationGroupMutation,
  useEditOrganizationGroupVehiclesMutation,
  useEditOrganizationGroupUsersMutation,
  useDeleteSingleGroupMutation,

  // account admin
  useEditOrganizationAccountMutation,

  // fleettag admin
  useOrganizationFleettagsQuery,
  useCreateOrganizationFleettagMutation,
  useSingleOrganizationFleettagQuery,
  useEditOrganizationFleettagMutation,
  useDeleteSingleFleettagMutation,

  // geozone admin
  useOrganizationGeozonesQuery,
  useLazyOrganizationGeozonesQuery,
  useCreateOrganizationGeozoneMutation,
  useSingleOrganizationGeozoneQuery,
  useEditOrganizationGeozoneMutation,
  useDeleteSingleGeozoneMutation,

  // geozone category admin
  useOrganizationGeozoneCategoriesQuery,
  useSingleOrganizationGeozoneCategoryQuery,
  useCreateOrganizationGeozoneCategoryMutation,
  useEditOrganizationGeozoneCategoryMutation,
  useDeleteOrganizationGeozoneCategoryMutation,
  useAssignGeozoneToGeozoneCategoryMutation,
  useAssignUserToGeozoneCategoryMutation,

  // vehicle alert
  useOrganizationVehicleAlertsQuery,
  useSingleOrganizationVehicleAlertQuery,

  // send message to driver
  useSendMessageMutation,

  // get driver assigned to vehicle
  useGetAssignedDriverQuery,

  // [deprecated] Reports
  useGetDetailedReportQuery, // deprecated

  // Camera
  useGetVehicleCameraDetailsQuery,
  useGetEventCameraRecordingQuery,
  useCameraRequestVideoMutation,
  useVisionMaxGetTokenQuery,

  // Manage Rule Alert
  useCreateOrganizationRuleAlertListMutation,
  useOrganizationRuleAlertListQuery,
  useSingleOrganizationRuleAlertQuery,
  useEditOrganizationRuleAlertMutation,
  useDeleteOrganiaztionRuleMutation,

  useGetSystemRuleQuery,
  useGetNotificationVariableListQuery,

  // Documents
  useOrganisationDocumentsQuery,
  useUploadOrganisationDocumentMutation,
  useOrganisationSingleDocumentQuery,
  useEditOrganisationDocumentMutation,

  // Global Search
  useGlobalSearchQuery,

  // Heatmap
  useHeatmapConfigQuery,
  useUpdateHeatmapConfigMutation,
  useHeatmapEventsQuery,
  // White labelling
  useGetWhiteLabelConfigListQuery,
  useLazyGetWhiteLabelConfigListQuery,
  useGetWhiteLabelConfigQuery,
  useCreateWhiteLabelConfigMutation,
  useUpdateWhiteLabelConfigMutation,
  useDeleteWhiteLabelConfigMutation,

  // generic status codes
  useGenericStatusCodesQuery,
  useLazyGenericStatusCodesQuery,

  // account event colours
  useAccountStatusCodeColorsQuery,
  useUpdateAccountStatusCodeColorMutation,
  useResetAccountStatusCodeColorMutation,

  // crash track
  useCrashTrackQuery,
  useLazyCrashTrackQuery,
  useCrashTrackAssetInfoQuery,
  useCrashTrackIncidentInfoQuery,
  useCrashTrackChartDataQuery,
  useCrashTrackEventReportQuery,

  // custom asset image
  useGetCustomAssetImageListQuery,
  useUploadCustomAssetImageMutation,
  useGetStandardAssetIconsListQuery,
} = AdminAPIs;
